import AlertQueue from '../AlertQueue';
import giftColorThresholds from '../GiftThresholds';
import util from '../util';
import MessageWidget from './MessageWidget';
import 'animate.css/animate.min.css';
import './chatWidget.scss';

export default class ChatWidget extends MessageWidget {
	constructor(opts) {
		super(opts);

		this.queue = new AlertQueue();
		this._currentItem = null;
		// Extra room held after a message is removed, shrunk a bit each frame so the
		// remaining messages slide into the gap instead of jumping.
		this._spacerPx = 0;
		this._scrollCarry = 0;
	}

	_build() {
		super._build();

		this.container.classList.add('chat-widget');
		this._injectGiftColorCSS();

		this.spacer = document.createElement('div');
		this.spacer.className = 'spacer';
		this.container.appendChild(this.spacer);

		this.queue.onSizeChange = size => this._applyChatSpeedup(size);
	}

	_audioRequired() { return !!(this.settings.enableTTS || this.settings.enableEventSounds); }

	async init(initialData = {}) {
		this._startScrollAnimation();
		await super.init(initialData);
	}

	destroy() {
		this._stopScrollAnimation();
		super.destroy();
	}

	// ── Settings ──────────────────────────────────────────────────────────────

	applySettings(settings) {
		const s = settings ? { ...settings } : {};
		if(typeof s.ignoreUsers === 'string')
			s.ignoreUsers = (s.ignoreUsers||'').split('\n').filter(Boolean);
		s.ignoreUsers = (s.ignoreUsers || []).map(a => a.toLowerCase());

		const oldOutTransition = this.settings.outTransition;
		const wasHorizontal = !!this.settings.horizontal;
		super.applySettings(s);
		this.container.classList.toggle('horizontal', !!this.settings.horizontal);
		this.container.classList.toggle('align-bottom', this.settings.alignment === 'bottom');
		if(wasHorizontal !== !!this.settings.horizontal) {
			// The spacer and the scroll offset live on the other axis now — start over.
			this._spacerPx = 0;
			this._scrollCarry = 0;
			this.spacer.style.width = '';
			this.spacer.style.height = '';
			this.container.scrollTop = 0;
			this.container.scrollLeft = 0;
		}
		this.queue.timeoutMs = s.loadTimeout || 5000;

		// remove old messages that had no out transition
		if(!oldOutTransition && settings.outTransition) {
			const messages = this.container.children;
			const ids = [];
			for(let i = 1; i < messages.length; i++) {
				if(messages[i].id)
				ids.push(messages[i].id);
			}
			for(let id of ids) this._removeMessage(id);
		}
	}


	_injectGiftColorCSS() {
		if(document.getElementById('chat-gift-color-thresholds-style')) return;
		const style = document.createElement('style');
		style.id = 'chat-gift-color-thresholds-style';
		style.textContent = giftColorThresholds.map(t => `
			.widget.chat-widget .gift.gift-${t.value} {
				color: ${util.getContrastingTextColor(t.color)};
				background-color: ${t.color};
			}
		`).join('');
		document.head.appendChild(style);
	}

	_applyChatSpeedup(size) {
		const minSpeedup = 5;
		const baseDuration = this.settings.duration || 500;

		if(size <= minSpeedup) {
			this.container.style.setProperty('--animate-duration', `${baseDuration}ms`);
			return;
		}

		const s = 1.0 + (size - minSpeedup) / 10.0;

		// Shorten animation duration proportionally (min 50ms)
		const newDuration = Math.max(50, Math.round(baseDuration / s));
		this.container.style.setProperty('--animate-duration', `${newDuration}ms`);

		if(!this._currentItem) return;
		try {
			if(s >= 2.0) {
				this._currentItem.sounds.length = 0; // drop all sounds (including any fallback)
			} else {
				for(const snd of this._currentItem.sounds) snd?.setPlaybackRate?.(s);
			}
		} catch(e) { /* ignore */ }
	}

	_generateCSS() {
		const id = this.container.id, s = this.settings;
		return `
			.widget.chat-widget#${id} {
				${s.paddingTop    ? `padding-top:       ${s.paddingTop}px;` : ''}
				${s.paddingLeft   ? `padding-left:     ${s.paddingLeft}px;` : ''}
				${s.paddingBottom ? `padding-bottom: ${s.paddingBottom}px;` : ''}
				${s.paddingRight  ? `padding-right:   ${s.paddingRight}px;` : ''}
			}
			.widget.chat-widget#${id} .message {
				${s.textBg ? `background-color: ${s.textBg};` : ''}
				${s.textColor ? `color: ${s.textColor};` : ''}
				${s.fontFamily ? `font-family: ${s.fontFamily};` : ''}
				${s.fontSize > 0 ? `font-size: ${s.fontSize}px;` : ''}
				${s.letterSpacing > 0 ? `letter-spacing: ${s.letterSpacing}px;` : ''}
				${s.textAlign ? `text-align: ${s.textAlign};` : ''}
				${s.boldText ? `font-weight: bold;` : ''}
				${s.italicText ? `font-style: italic;` : ''}
				${s.underlineText || s.strikethroughText ? `text-decoration: ${s.underlineText ? 'underline' : ''} ${s.strikethroughText ? 'line-through' : ''};` : ''}
				${s.uppercaseText ? `text-transform: uppercase;` : ''}
				${s.blurRadius > 0 || s.spreadRadius > 0 ? `filter: ${util.generateSpreadShadowFilter(s.spreadRadius, s.blurRadius, s.shadowColor)};` : ''}
			}
			.widget.chat-widget#${id} .message.isGift {
				${s.giftBg ? `background-color: ${s.giftBg};` : ''}
				${s.giftColor ? `color: ${s.giftColor};` : ''}
			}
			.widget.chat-widget#${id} .message .avatar, .widget.chat-widget .message .service-icon {
				${s.fontSize > 0 ? `width: ${s.fontSize}px;` : ''}
				${s.fontSize > 0 ? `height: ${s.fontSize}px;` : ''}
			}
			.widget.chat-widget#${id} > .message .chatImage {
				${s.fontSize > 0 ? `max-width: ${s.fontSize * 3}px;` : ''}
				${s.fontSize > 0 ? `max-height: ${s.fontSize * 3}px;` : ''}
			}
			${s.enableSpeakingHighlight && s.speakingOutlineColor ? `
			.widget.chat-widget#${id} > .message.speaking {
				filter: ${util.generateSpreadShadowFilter(s.spreadRadius || 2, s.blurRadius || 10, s.speakingOutlineColor)};
			}` : ''}
			${s.enableLoadingHighlight && s.loadingOutlineColor ? `
			.widget.chat-widget#${id} > .message.loading {
				filter: ${util.generateSpreadShadowFilter(s.spreadRadius || 2, s.blurRadius || 10, s.loadingOutlineColor)};
			}` : ''}
			${giftColorThresholds.map(t => `
				.widget.chat-widget#${id} .gift.gift-${t.value} {
					color: ${util.getContrastingTextColor(t.color)};
					background-color: ${t.color};
				}
			`).join('')}
			${s.extraCSS || ''}
		`;
	}

	// ── Chat message handling ─────────────────────────────────────────────────

	_onChat(data) {
		if(data.type === 'delete') {
			for(const msgId of data.ids) {
				const itemId = `${data.serviceId}-${msgId}`;
				const removed = this.queue.cancel(it => it.prepared.id === itemId);
				for(const item of removed) item.sounds?.forEach(s => s.stop());
				this._removeMessage(itemId, true);
			}
			return;
		} else if(data.type == 'clear') {
			const removed = this.queue.cancel(it => it.prepared.serviceId === data.serviceId && it.prepared.channelToken == data.target?.channelToken && it.prepared.chatToken == data.target?.chatToken);
			for(const item of removed) item.sounds?.forEach(s => s.stop());
			for(let i = 0; i < this.container.childNodes.length; i++) {
				const child = this.container.childNodes[i];
				if(child.dataset.serviceId == data.serviceId && child.dataset.channelToken == data.target?.channelToken && child.dataset.chatToken == data.target?.chatToken)
					this.container.childNodes[i--].remove();
			}

			return;
		}

		if(this.settings.excludedChannels?.includes(`${data.serviceId}:${data.target?.channelToken}`)) return;

		const displayName = data.sender?.displayName || data.sender?.username || data.sender?.token || '';
		if(displayName && (this.settings.ignoreUsers || []).indexOf(displayName.toLowerCase()) >= 0) return;

		if(!this.settings.showBotMessages && data.sender?.isSelf) return;
		if(!this.settings.showUserCommands && data.isCommand) return;

		const prepared = util.prepareMessage(data, {
			id: true, serviceId: true, serviceName: true, serviceIconUrl: true,
			channelToken: true, chatToken: true,
			avatarUrl: true, displayName: true, spansHtml: true, giftString: true,
			eventString: true, classList: true, giftColorThreshold: true,
			sounds: true,
			group: true, groupId: true, images: true, consoleLog: true,
		}, {
			giftTypes: this.giftTypes,
			eventTypes: this.eventTypes,
			serviceIcons: this.serviceIcons,
			user: this.userData,
			giftColorThresholds,
			giftMediaThresholds: this.giftThresholds,
			textReplacements: this.textReplacements,
		});

		console.log.apply(null, prepared.consoleLog);

		if(prepared.group) {
			const allMessages = Array.from(this.container.children);
			const lastInGroup = allMessages.findLast(el => el.dataset.groupId === prepared.groupId);
			if(lastInGroup) {
				data.multiplier ||= 1;
				data.multiplier += parseInt(lastInGroup.dataset.multiplier || '1');
				lastInGroup.dataset.multiplier = String(data.multiplier);
				const giftElem = lastInGroup.querySelector('.gift-string');
				if(giftElem) giftElem.textContent = ` ${util.generateGiftString(data, this.giftTypes?.[data.serviceId]?.[data.giftType]).displayString}`;

				if(prepared.images?.length)
					util.placeChatImages(lastInGroup, prepared.images.filter(img => img?.url).map(img => img.url));

				return;
			}
		}

		for(let i = 0; i < 10; i++) {
			if(!document.getElementById(prepared.id)) break;
			prepared.id = util.generateRandomString(20);
		}
		prepared.classList.push('animate__animated');

		const maxMessages = 50;
		const messages = this.container.children;
		while(messages.length > maxMessages) {
			const toRemove = messages[1]; // messages[0] is the spacer
			const next = messages[2];
			this._growSpacer(this.settings.horizontal
				? (next ? next.offsetLeft - toRemove.offsetLeft : toRemove.offsetWidth)
				: (next ? next.offsetTop - toRemove.offsetTop : toRemove.offsetHeight));
			toRemove.remove();
		}

		const msgElem = this._createMessageElement(prepared);
		if(this.settings.inTransition)
			msgElem.classList.add(`animate__${this.settings.inTransition}`);
		if(prepared.groupId)
			msgElem.dataset.multiplier = String(data.multiplier||1);
		this.container.appendChild(msgElem);

		const item = { prepared, elem: msgElem };

		this.queue.append({
			data:    item,
			preload: signal => this._preloadChat(item, signal),
			play:    signal => this._processChat(item, signal),
		});
	}

	async _preloadChat(item, signal) {
		if(this.queue.pendingItems.length >= 15) return;
		if(!this.settings.enableTTS && !this.settings.enableEventSounds) return;

		item.elem.classList.add('loading');
		try {
			const loadSounds = [];
			const loadSoundsRecursive = async sounds => {
				try {
					if(Array.isArray(sounds)) {
						for(let sound of sounds) {
							if(await loadSoundsRecursive(sound)) return true;
						}
						throw new Error('Could not load a sound');
					}

					if(sounds.type == 'any') {
						let loaded = 0;
						for(let sound of sounds.items) {
							if(await loadSoundsRecursive(sound)) loaded++;
						}
						if(!loaded) throw new Error('Could not load any sound');
						return true;
					}

					let category = 'event';
					if(sounds.type == 'tts') {
						if(!this.settings.enableTTS) return false;
						category = 'tts';
					} else if(sounds.type == 'song') {
						throw new Error(`Unknown sound type ${sounds.type}`);
					} else {
						if(!this.settings.enableEventSounds) return false;
					}

					let p;
					if(sounds.url) p = this.soundManager.createUrlSound(sounds.proxy ? `${this.baseUrl}/proxy/${encodeURIComponent(sounds.url)}` : sounds.url);
					else if(sounds.type == 'tts') {
						p = this.soundManager.createTtsSound(sounds.speak, sounds.voice&&sounds.provider||'vampiTTS', sounds.provider&&sounds.voice||'IVONA 2 Voice Brian22', sounds.rate, sounds.pitch, sounds.style, sounds.role);
					} else if(sounds.type == 'mml') {
						p = this.soundManager.createMmlSound(sounds.mml, sounds.instrument);
					}
					p.category = category;
					await p.load(signal);
					loadSounds.push(p);
					return true;
				} catch(e) {
					console.error('Could not load sound', sounds, e);
					return false;
				}
			};
			try {
				await loadSoundsRecursive(item.prepared.sounds);
				item.sounds = loadSounds;
			} catch(e) {
				console.error('Could not load any sounds', e);
			}
		} finally {
			item.elem.classList.remove('loading');
		}
	}

	async _processChat(item, signal) {
		item.elem.classList.add('speaking');
		this._currentItem = item;
		this._applyChatSpeedup(this.queue.pendingItems.length);
		try {
			if(this.queue.pendingItems.length >= 15) throw 'queue full';
			if(!this.settings.enableTTS && !this.settings.enableEventSounds) throw 'sounds disabled';
			if(!item.sounds) throw 'no sounds';
			for(const s of item?.sounds) {
				if(s.category == 'tts' && !this.settings.enableTTS) continue;
				if(s.category == 'event' && !this.settings.enableEventSounds) continue;
				try { await s.play(signal); } catch(e) {
					if(!(e instanceof DOMException && e.name === 'AbortError')) console.error('Could not play sound', s, e);
				}
				if(signal?.aborted) break;
			}
		} catch(e) {
			//
		} finally {
			this._currentItem = null;
		}
		item.elem.classList.remove('speaking');

		if(this.settings.outTransition)
			setTimeout(() => this._removeMessage(item.prepared.id), this.settings.linger || 8000);
	}

	// ── DOM helpers ───────────────────────────────────────────────────────────

	_createMessageElement(prepared) {
		const div = document.createElement('div');
		div.id = prepared.id;
		if(prepared.groupId) div.dataset.groupId = prepared.groupId;
		div.dataset.multiplier = '1';
		for(const cls of (prepared.classList || [])) div.classList.add(cls);
		div.dataset.serviceId = prepared.serviceId;
		div.dataset.channelToken = prepared.channelToken;
		div.dataset.chatToken = prepared.chatToken;
		div.dataset.chatterToken = prepared.chatterToken;

		if(prepared.serviceIconUrl) {
			const icon = document.createElement('img');
			icon.src = prepared.serviceIconUrl;
			icon.alt = prepared.serviceName || '';
			icon.className = 'service-icon';
			div.appendChild(icon);
		} else if(prepared.serviceName) {
			const small = document.createElement('small');
			small.className = 'platform';
			small.textContent = prepared.serviceName;
			div.appendChild(small);
		}

		if(prepared.avatarUrl) {
			const avatar = document.createElement('img');
			avatar.src = prepared.avatarUrl;
			avatar.alt = '';
			avatar.className = 'avatar';
			div.appendChild(avatar);
		}

		const name = document.createElement('b');
		name.textContent = prepared.displayName;
		div.appendChild(name);

		if(prepared.giftString) {
			const giftElem = document.createElement('b');
			giftElem.className = 'gift-string';
			giftElem.textContent = ` ${prepared.giftString}`;
			div.appendChild(giftElem);
		}

		if(prepared.eventString) {
			const eventElem = document.createElement('span');
			eventElem.textContent = ` ${prepared.eventString}`;
			div.appendChild(eventElem);
		}

		if(prepared.spansHtml) {
			const spansElem = document.createElement('span');
			spansElem.innerHTML = `: ${prepared.spansHtml}`;
			div.appendChild(spansElem);
		}

		const clearfix = document.createElement('div');
		clearfix.className = 'clearfix';
		div.appendChild(clearfix);

		if(prepared.images?.length)
			util.placeChatImages(div, prepared.images.filter(img => img?.url).map(img => img.url));

		return div;
	}

	_removeMessage(id, force) {
		const el = document.getElementById(id);
		if(!el) return;
		// Whatever room the message gave up goes to the spacer, on the scrolling axis
		const gap = this.settings.horizontal ? el.offsetWidth : el.offsetHeight;
		if(this.settings.outTransition && !force) {
			el.classList.remove(`animate__${this.settings.inTransition}`);
			// Snapshot layout before taking element out of flow
			const top = el.offsetTop;
			const left = el.offsetLeft;
			const width = el.offsetWidth;
			const height = el.offsetHeight;
			// Add to spacer now — it starts shrinking while the animation plays
			this._growSpacer(gap);
			// Pin element in place visually while it plays its exit animation
			el.style.position = 'absolute';
			el.style.top = top + 'px';
			el.style.left = left + 'px';
			el.style.width = width + 'px';
			el.style.height = height + 'px';
			el.classList.add(`animate__${this.settings.outTransition}`);
			const fallback = setTimeout(() => el.remove(), (this.settings.duration || 500) + 500);
			el.addEventListener('animationend', () => { clearTimeout(fallback); el.remove(); }, { once: true });
		} else {
			this._growSpacer(gap);
			el.remove();
		}
	}

	// ── Scroll animation ──────────────────────────────────────────────────────

	_startScrollAnimation() {
		const frame = () => {
			if(!this._animFrameId) return;
			this._animFrameId = requestAnimationFrame(frame);
			this._stepScroll();
		};
		this._animFrameId = requestAnimationFrame(frame);
	}

	// Fraction of the remaining distance covered each frame — the "speed" setting.
	_getScrollSpeed() {
		const speed = Number(this.settings.scrollSpeed);
		if(!(speed > 0)) return 0.2;
		return Math.min(1, speed);
	}

	// Move the scroll offset by a fraction of what is left. The offset itself only ever
	// changes by whole pixels — the sub-pixel remainder is kept here and spent on a later
	// frame, so a slow setting still moves without ever landing the text between pixels.
	_advanceScroll(axis, diff, speed) {
		if(diff < 1) {
			this._scrollCarry = 0;
			return;
		}
		this._scrollCarry += diff * speed;
		const step = Math.trunc(this._scrollCarry);
		if(!step) return;
		this._scrollCarry -= step;
		this.container[axis] += step;
	}

	_stepScroll() {
		const el = this.container;
		if(!this.spacer) return;
		const speed = this._getScrollSpeed();
		const last = el.lastElementChild;
		const hasMessages = last && last !== this.spacer;

		// Decays towards zero at the same rate the scroll catches up, so a removed message's
		// gap closes at the speed everything else moves. Snap the last half pixel shut.
		this._spacerPx -= this._spacerPx * speed;
		if(this._spacerPx < 0.5) this._spacerPx = 0;

		if(this.settings.horizontal) {
			this._applySpacer();
			if(!hasMessages) return;
			const paddingRight = parseInt(getComputedStyle(el).paddingRight) || 0;
			const target = last.offsetLeft + last.offsetWidth + paddingRight - el.clientWidth;
			this._advanceScroll('scrollLeft', target - el.scrollLeft, speed);
		} else {
			const paddingBottom = parseInt(getComputedStyle(el).paddingBottom) || 0;
			// Bottom alignment: while the messages are shorter than the widget, hold the
			// spacer at the leftover height so they sit on the bottom edge. Once they
			// overflow there is no leftover and this is a no-op.
			let floor = 0;
			if(hasMessages && this.settings.alignment === 'bottom') {
				const messagesHeight = last.offsetTop + last.offsetHeight - this.spacer.offsetHeight;
				floor = Math.max(0, el.clientHeight - paddingBottom - messagesHeight);
			}
			this._applySpacer(floor);
			if(!hasMessages) return;
			const target = last.offsetTop + last.offsetHeight + paddingBottom - el.clientHeight;
			this._advanceScroll('scrollTop', target - el.scrollTop, speed);
		}
	}

	// The spacer takes the removed messages' room on whichever axis the chat scrolls;
	// in horizontal mode its height is left to the stylesheet (it doubles as the strut
	// that gives the line box the full widget height).
	//
	// Whole pixels only. The spacer sits in front of every message, so a fractional size
	// puts the whole line on a fractional offset — and since it decays a little each frame,
	// that offset lands somewhere new every frame and the text shimmers as it is
	// re-rasterised. The fractional part lives on in _spacerPx, which is what decays.
	_applySpacer(floor = 0) {
		const px = Math.round(Math.max(this._spacerPx, floor));
		if(this.settings.horizontal) {
			this.spacer.style.width = px + 'px';
			this.spacer.style.height = '';
		} else {
			this.spacer.style.height = px + 'px';
			this.spacer.style.width = '';
		}
	}

	_growSpacer(px) {
		if(!(px > 0)) return;
		this._spacerPx += px;
		this._applySpacer();
	}

	_stopScrollAnimation() {
		if(this._animFrameId) {
			cancelAnimationFrame(this._animFrameId);
			this._animFrameId = null;
		}
	}
}
